/**
 * First-touch UTM attribution capture (client-side).
 *
 * Marketing drives people IG/TikTok -> Substack -> VolleyBridge. To see who
 * converts from free subscriber to paid member we need to know which content
 * sent them. This module captures UTM params (+ referrer + landing path) on
 * the first visit and persists them for ~90 days so the value survives the
 * weeks between subscribing and paying.
 *
 *   - First-touch is written ONCE and never overwritten.
 *   - Last-touch is refreshed on every later UTM-tagged visit.
 *   - Stored in BOTH a first-party cookie and localStorage for resilience.
 *
 * At signup, getStoredAttribution() is read by the signup form and sent to the
 * invite route, which writes it onto the new profiles row.
 */

const FIRST_KEY = "vb_attr_first";
const LAST_KEY = "vb_attr_last";
const MAX_AGE_DAYS = 90;

export interface AttrTouch {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  referrer: string | null;
  landing_page: string | null;
  ts: string; // ISO timestamp
}

/** DB-shaped payload sent to the server at signup. */
export interface StoredAttribution {
  first_utm_source: string | null;
  first_utm_medium: string | null;
  first_utm_campaign: string | null;
  first_utm_content: string | null;
  first_referrer: string | null;
  first_landing_page: string | null;
  first_seen_at: string | null;
  last_utm_source: string | null;
  last_utm_medium: string | null;
  last_utm_campaign: string | null;
  last_utm_content: string | null;
}

/** Normalize a UTM value to our lowercase convention (or null). */
function lc(v: string | null): string | null {
  if (!v) return null;
  const t = v.trim().toLowerCase();
  return t.length ? t : null;
}

function setCookie(name: string, value: string): void {
  if (typeof document === "undefined") return;
  const maxAge = MAX_AGE_DAYS * 24 * 60 * 60;
  const secure =
    typeof location !== "undefined" && location.protocol === "https:"
      ? "; secure"
      : "";
  document.cookie = `${name}=${encodeURIComponent(
    value,
  )}; path=/; max-age=${maxAge}; samesite=lax${secure}`;
}

function getCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(
    new RegExp("(?:^|; )" + name + "=([^;]*)"),
  );
  return match ? decodeURIComponent(match[1]) : null;
}

function read(key: string): AttrTouch | null {
  let raw: string | null = getCookie(key);
  if (!raw) {
    try {
      raw = typeof localStorage !== "undefined" ? localStorage.getItem(key) : null;
    } catch {
      raw = null;
    }
  }
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AttrTouch;
  } catch {
    return null;
  }
}

function write(key: string, touch: AttrTouch): void {
  const raw = JSON.stringify(touch);
  setCookie(key, raw);
  try {
    if (typeof localStorage !== "undefined") localStorage.setItem(key, raw);
  } catch {
    /* localStorage may be unavailable (private mode) — cookie still set */
  }
}

/**
 * Capture attribution for the current visit. Safe to call on every page load.
 * Writes first-touch only once; refreshes last-touch when this visit carries a
 * UTM. Call from a client component's useEffect.
 */
export function captureAttribution(): void {
  if (typeof window === "undefined") return;

  const params = new URLSearchParams(window.location.search);
  const utm = {
    utm_source: lc(params.get("utm_source")),
    utm_medium: lc(params.get("utm_medium")),
    utm_campaign: lc(params.get("utm_campaign")),
    utm_content: lc(params.get("utm_content")),
  };
  const hasUtm = Boolean(
    utm.utm_source || utm.utm_medium || utm.utm_campaign || utm.utm_content,
  );

  const touch: AttrTouch = {
    ...utm,
    referrer: document.referrer || null,
    landing_page: window.location.pathname || null,
    ts: new Date().toISOString(),
  };

  // First-touch: written once, never overwritten.
  if (!read(FIRST_KEY)) write(FIRST_KEY, touch);

  // Last-touch: refresh whenever a new UTM-tagged visit happens.
  if (hasUtm) write(LAST_KEY, touch);
}

/** Read stored attribution in DB-column shape. All null if nothing captured. */
export function getStoredAttribution(): StoredAttribution {
  const f = read(FIRST_KEY);
  const l = read(LAST_KEY);
  return {
    first_utm_source: f?.utm_source ?? null,
    first_utm_medium: f?.utm_medium ?? null,
    first_utm_campaign: f?.utm_campaign ?? null,
    first_utm_content: f?.utm_content ?? null,
    first_referrer: f?.referrer ?? null,
    first_landing_page: f?.landing_page ?? null,
    first_seen_at: f?.ts ?? null,
    last_utm_source: l?.utm_source ?? null,
    last_utm_medium: l?.utm_medium ?? null,
    last_utm_campaign: l?.utm_campaign ?? null,
    last_utm_content: l?.utm_content ?? null,
  };
}
